"use client";

/**
 * Browser-side adapters wiring @app/studio-runtime into:
 *   - Navigation  (consumer supplies the navigation fn)
 *   - Storage     (localStorage)
 *   - Auth        (localStorage-backed JSON blob)
 *   - Transport   (fetch → /api/operations/[operationId])
 */

import type {
  AuthAdapter,
  AuthCredentials,
  NavigationAdapter,
  NavTarget,
  StorageAdapter,
} from "../context/execution-context";

export type NavigationFn = (target: NavTarget) => void;

/**
 * Legacy private key. Only used when no app-scoped key is supplied.
 * Apps persist their canonical session blob under `<appSlug>.session`
 * (e.g. "mental2.session"); the auth adapter must target the same key
 * so credentials written by flows and by auth:write stay in sync.
 */
const AUTH_STORAGE_KEY = "studio.auth";

export interface BrowserAuthAdapterOptions {
  /**
   * localStorage key backing the credentials. Defaults to "studio.auth";
   * pass `<appSlug>.session` to share the app's canonical session blob.
   */
  storageKey?: string;
}

function safeLocalStorage(): Storage | null {
  try {
    if (typeof window === "undefined") return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

function createStorageReadCache(): {
  read(key: string): string | null;
  invalidate(key: string): void;
  clear(): void;
} {
  const cache = new Map<string, string | null>();

  if (typeof window !== "undefined") {
    window.addEventListener("storage", (event) => {
      if (event.storageArea === safeLocalStorage()) {
        if (event.key === null) cache.clear();
        else cache.delete(event.key);
      }
    });
  }

  return {
    read(key) {
      if (cache.has(key)) return cache.get(key) ?? null;

      const ls = safeLocalStorage();
      if (!ls) return null;

      try {
        const value = ls.getItem(key);
        cache.set(key, value);
        return value;
      } catch {
        return null;
      }
    },
    invalidate(key) {
      cache.delete(key);
    },
    clear() {
      cache.clear();
    },
  };
}

export function createBrowserStorageAdapter(): StorageAdapter {
  const readCache = createStorageReadCache();

  return {
    read(key) {
      return readCache.read(key);
    },
    write(key, value) {
      const ls = safeLocalStorage();
      if (!ls) return;
      try {
        ls.setItem(key, value);
        readCache.invalidate(key);
      } catch {
        // Ignore quota and security errors.
      }
    },
    delete(key) {
      const ls = safeLocalStorage();
      if (!ls) return;
      try {
        ls.removeItem(key);
        readCache.invalidate(key);
      } catch {
        // Ignore security errors.
      }
    },
  };
}

function isAuthCredentials(value: unknown): value is AuthCredentials {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as { userId?: unknown }).userId === "string" &&
    typeof (value as { accessToken?: unknown }).accessToken === "string"
  );
}

export function createBrowserAuthAdapter(
  options: BrowserAuthAdapterOptions = {},
): AuthAdapter {
  // No in-memory read cache here: flows write the same key through the
  // storage adapter in this tab (storage events do not fire for same-tab
  // writes), so a cached read would serve stale credentials on the next
  // screen mount and trip the auth gate. localStorage reads are cheap.
  const storageKey =
    typeof options.storageKey === "string" && options.storageKey.trim()
      ? options.storageKey.trim()
      : AUTH_STORAGE_KEY;

  const readRaw = (): string | null => {
    const ls = safeLocalStorage();
    if (!ls) return null;
    try {
      return ls.getItem(storageKey);
    } catch {
      return null;
    }
  };

  const readParsedRecord = (): Record<string, unknown> | null => {
    const raw = readRaw();
    if (!raw) return null;
    try {
      const parsed: unknown = JSON.parse(raw);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
      return null;
    } catch {
      return null;
    }
  };

  return {
    read(): AuthCredentials | null {
      const parsed: unknown = (() => {
        const raw = readRaw();
        if (!raw) return null;
        try {
          return JSON.parse(raw);
        } catch {
          return null;
        }
      })();
      return isAuthCredentials(parsed) ? parsed : null;
    },
    write(creds) {
      const ls = safeLocalStorage();
      if (!ls) return;
      try {
        // Merge over the existing app session blob so auxiliary keys the
        // flows maintain (onboardingComplete, chatSessionId, …) survive an
        // auth:write that only carries credential fields.
        const existing = readParsedRecord() ?? {};
        ls.setItem(
          storageKey,
          JSON.stringify({ ...existing, ...creds }),
        );
      } catch {
        // Ignore quota and security errors.
      }
    },
    clear() {
      const ls = safeLocalStorage();
      if (!ls) return;
      try {
        ls.removeItem(storageKey);
      } catch {
        // Ignore security errors.
      }
    },
  };
}

export function createNavigationAdapter(navigate: NavigationFn): NavigationAdapter {
  return { navigate };
}

/** Fetch-based transport for /api/operations/[operationId]. */
export { createFetchCallTransport } from "../createFetchCallTransport";